import React from 'react'
import type { OpportunityItem } from '../types'
import { useOportunidadesFiltersStore } from '../stores/oportunidadesFiltersStore'
import { useInOutEnabled } from '@/modules/in-out/hooks/useInOutEnabled'
import type { InOutMap } from '@/modules/in-out/services/inOutService'
import { fetchInOutMap } from '@/modules/in-out/services/inOutService'
import { subscribeInOutUpdates } from '@/modules/in-out/hooks/useInOutStatus'

export type OpportunityEntryZoneMap = Record<
  string,
  { inEntryZone: boolean; hasEntryZone: boolean }
>

function pctFor(it: OpportunityItem): number {
  const v = it.thermometerPercent ?? it.confluencePercent ?? 0
  const n = Number(v)
  return Number.isFinite(n) ? Math.max(0, Math.min(100, Math.round(n))) : 0
}

export function useFilteredOportunidades(
  items: OpportunityItem[],
  entryZoneMap?: OpportunityEntryZoneMap,
) {
  const filters = useOportunidadesFiltersStore()
  const inOutEnabled = useInOutEnabled()
  const [inOutMap, setInOutMap] = React.useState<InOutMap | null>(null)
  const [inOutLoaded, setInOutLoaded] = React.useState(false)

  React.useEffect(() => {
    let active = true

    async function loadInOut() {
      if (filters.inOutMode !== 'in' || !inOutEnabled) {
        if (!active) return
        setInOutMap(null)
        setInOutLoaded(false)
        return
      }

      const ids = items
        .map((it) => Number(it.id))
        .filter((id) => Number.isFinite(id))

      if (!ids.length) {
        if (!active) return
        setInOutMap({})
        setInOutLoaded(true)
        return
      }

      try {
        const map = await fetchInOutMap(ids)
        if (!active) return
        setInOutMap(map)
        setInOutLoaded(true)
      } catch {
        if (!active) return
        setInOutMap({})
        setInOutLoaded(true)
      }
    }

    loadInOut()

    return () => {
      active = false
    }
  }, [filters.inOutMode, inOutEnabled, items])

  // Mantem o mapa em sincronia em tempo real quando o usuario marca
  // "Estou dentro" / "Estou fora" sem precisar recarregar a pagina.
  React.useEffect(() => {
    if (!inOutEnabled) return undefined

    const unsubscribe = subscribeInOutUpdates(({ opportunityId, isIn }) => {
      setInOutMap((prev) => {
        const base = prev || {}
        const key = String(opportunityId)
        if (base[key] === isIn) return base
        return { ...base, [key]: isIn }
      })
    })

    return unsubscribe
  }, [inOutEnabled])

  const filtered = React.useMemo(() => {
    const term = String(filters.search || '').trim().toLowerCase()
    const sel = items
      .filter((it) => {
        if (filters.objective === 'long' && it.objective !== 'Long') return false
        if (filters.objective === 'short' && it.objective !== 'Short') return false
        // Timeframe alvo: aceita múltiplas seleções
        const tfs = filters.timeframes || ['all']
        if (!tfs.includes('all')) {
          const tf = String(it.fractalUpper || '').trim()
          if (!tfs.includes(tf as any)) return false
        }
        if (filters.minPercent > 0) {
          if (pctFor(it) < filters.minPercent) return false
        }
        if (filters.nearBreakOnly && !it.nearBreak) return false
        if (filters.uiWarningOnly && !it.uiWarning) return false

        // Zona de entrada (usa mapa derivado do módulo de Sinais)
        if (filters.entryZone !== 'all') {
          const key = String(it.id)
          const flags = entryZoneMap?.[key]
          // Sem zona de entrada válida: não entra em filtros "inside"/"outside"
          if (!flags || !flags.hasEntryZone) return false
          if (filters.entryZone === 'inside' && !flags.inEntryZone) return false
          if (filters.entryZone === 'outside' && flags.inEntryZone) return false
        }

        if (filters.inOutMode === 'in') {
          if (!inOutEnabled || !inOutLoaded) return false
          const key = String(it.id)
          if (!inOutMap || !inOutMap[key]) return false
        }

        if (term) {
          const sym = (it.symbol || '').toLowerCase()
          const name = (it.name || '').toLowerCase()
          if (!sym.includes(term) && !name.includes(term)) return false
        }
        return true
      })
      .sort((a, b) => {
        const ta = a.publishedAt ? Date.parse(a.publishedAt) : 0
        const tb = b.publishedAt ? Date.parse(b.publishedAt) : 0

        if (filters.sort === 'symbol') {
          const bySymbol = String(a.symbol).localeCompare(String(b.symbol))
          if (bySymbol !== 0) return bySymbol
          // Empate de símbolo: mais recente primeiro
          return tb - ta
        }

        if (filters.sort === 'relevance') {
          // Relevância: percent desc como critério principal
          const pa = pctFor(a)
          const pb = pctFor(b)
          if (pb !== pa) return pb - pa
          // Empate de relevância: mais recente primeiro
          return tb - ta
        }

        // Padrão: ordenar por data (mais recentes primeiro)
        if (tb !== ta) {
          return tb - ta
        }
        // Empate de data: ordena por símbolo para estabilidade
        return String(a.symbol).localeCompare(String(b.symbol))
      })

    return sel
  }, [
    items,
    entryZoneMap,
    inOutEnabled,
    inOutLoaded,
    inOutMap,
    filters.inOutMode,
    filters.objective,
    filters.timeframes,
    filters.minPercent,
    filters.nearBreakOnly,
    filters.uiWarningOnly,
    filters.entryZone,
    filters.search,
    filters.sort,
  ])

  return { filtered }
}
